

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

export default function AgentDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab]               = useState('ready');
  const [farmers,    setFarmers]    = useState([]);
  const [buyers,     setBuyers]     = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [readyCrops, setReadyCrops] = useState([]);
  const [readyFilter,  setReadyFilter]  = useState({ category:'', location:'' });
  const [readyLoading, setReadyLoading] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Load critical data first — ready crops and my requests
      const [rc, my] = await Promise.allSettled([
        api.get('/crops/ready?limit=30'),
        api.get('/requests/my?status=open'),
      ]);
      setReadyCrops(rc.value?.data?.crops    || []);
      setMyRequests(my.value?.data?.requests || []);
      setLoading(false); // ← show page immediately

      // Load farmers and buyers in background
      const [f, b] = await Promise.allSettled([
        api.get('/users/farmers?limit=30'),
        api.get('/users/buyers?limit=30'),
      ]);
      setFarmers(f.value?.data?.farmers || []);
      setBuyers(b.value?.data?.buyers   || []);
    } catch {
      setLoading(false);
    }
  };

  const loadReadyCrops = async (filters = readyFilter) => {
    setReadyLoading(true);
    try {
      const params = new URLSearchParams({ limit: 30 });
      if (filters.category) params.set('category', filters.category);
      if (filters.location) params.set('location', filters.location);
      const { data } = await api.get('/crops/ready?' + params);
      setReadyCrops(data.crops || []);
    } catch {} finally { setReadyLoading(false); }
  };

  const updateReadyFilter = (key, val) => {
    const updated = { ...readyFilter, [key]: val };
    setReadyFilter(updated);
    loadReadyCrops(updated);
  };

  const filterList = (list) => {
    if (!search) return list;
    return list.filter(u =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.locationAddress?.toLowerCase().includes(search.toLowerCase()) ||
      u.mobile?.includes(search)
    );
  };

  return (
    <div style={{ paddingBottom: 90 }}>

      {/* Header */}
      <div className="header" style={{ paddingBottom:'var(--sp-4)' }}>
        <div className="header__row">
          <div>
            <div className="header__title">🤝 Agent Dashboard</div>
            <div className="header__subtitle">Ready crops, requests, farmers & buyers</div>
          </div>
          <button className="header__action" onClick={loadAll}>🔄</button>
        </div>

        {/* Summary stats — clickable to switch tabs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginTop:12 }}>
          {[
            { id:'ready',      label:'Ready',   value:readyCrops.length,  color:'#D1FAE5', textColor:'#065F46' },
            { id:'myrequests', label:'My Req.', value:myRequests.length,  color:'#FED7AA', textColor:'#C2410C' },
            { id:'farmers',    label:'Farmers', value:farmers.length,     color:'#BBF7D0', textColor:'#166534' },
            { id:'buyers',     label:'Buyers',  value:buyers.length,      color:'#BFDBFE', textColor:'#1D4ED8' },
          ].map(s => (
            <div key={s.id} onClick={() => { setTab(s.id); setSearch(''); }}
              style={{ background: tab===s.id ? s.textColor : s.color, borderRadius:10, padding:'8px', textAlign:'center', cursor:'pointer',
                border: tab===s.id ? `2px solid ${s.textColor}` : '2px solid transparent', transition:'all 0.2s' }}>
              <div style={{ fontSize:18, fontWeight:700, color: tab===s.id ? 'white' : s.textColor }}>{s.value}</div>
              <div style={{ fontSize:10, color: tab===s.id ? 'white' : s.textColor, fontWeight:600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div style={{ background:'white', padding:'10px 16px', borderBottom:'1px solid var(--gray-100)', display:'flex', gap:8 }}>
        <input className="input"
          placeholder={tab==='ready' ? '🔍 Search crops...' : tab==='myrequests' ? '🔍 Search requests...' : `🔍 Search ${tab}...`}
          value={search} onChange={e => setSearch(e.target.value)} style={{ flex:1 }} />
      </div>

      <div style={{ maxWidth:480, margin:'0 auto', padding:'16px' }}>
        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:50 }}>
            <div className="spinner" style={{ width:32, height:32 }} />
          </div>
        ) : (
          <>
            {/* ── READY TO SELL TAB ── */}
            {tab === 'ready' && (
              <>
                {/* Filters */}
                <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
                  <select className="input" style={{ flex:1, minWidth:120 }}
                    value={readyFilter.category}
                    onChange={e => updateReadyFilter('category', e.target.value)}>
                    <option value="">All Categories</option>
                    {['vegetables','fruits','grains','spices','flowers','green leafs','other'].map(c => (
                      <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>
                    ))}
                  </select>
                  <input className="input" style={{ flex:1, minWidth:120 }}
                    placeholder=" Location..."
                    value={readyFilter.location}
                    onChange={e => updateReadyFilter('location', e.target.value)} />
                  {(readyFilter.category || readyFilter.location) && (
                    <button className="btn btn--outline btn--sm"
                      onClick={() => { setReadyFilter({ category:'', location:'' }); loadReadyCrops({ category:'', location:'' }); }}>
                      ✕ Clear
                    </button>
                  )}
                </div>

                <div style={{ marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:13, color:'var(--gray-400)' }}>
                    {readyLoading ? 'Loading...' : `${readyCrops.filter(c => !search || c.cropName?.toLowerCase().includes(search.toLowerCase()) || c.userId?.name?.toLowerCase().includes(search.toLowerCase())).length} ready to sell`}
                  </span>
                  <button className="btn btn--outline btn--sm" onClick={() => loadReadyCrops()}>🔄 Refresh</button>
                </div>

                {readyLoading ? (
                  <div style={{ display:'flex', justifyContent:'center', padding:30 }}>
                    <div className="spinner" style={{ width:28, height:28 }} />
                  </div>
                ) : readyCrops
                    .filter(c => !search ||
                      c.cropName?.toLowerCase().includes(search.toLowerCase()) ||
                      c.userId?.name?.toLowerCase().includes(search.toLowerCase()) ||
                      c.locationAddress?.toLowerCase().includes(search.toLowerCase()))
                    .map(crop => {
                      const stage = crop.latestStage || crop.stages?.[crop.stages?.length - 1];
                      const thumb = stage?.mediaUrls?.find(u => u && !u.startsWith('data:'));
                      return (
                        <div key={crop._id} className="card"
                          style={{ marginBottom:10, overflow:'hidden', cursor:'pointer' }}
                          onClick={() => navigate(`/crop/${crop._id}`)}>
                          {thumb && (
                            <img src={thumb} alt={crop.cropName}
                              style={{ width:'100%', height:140, objectFit:'cover' }} />
                          )}
                          <div style={{ padding:'10px 14px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                              <div>
                                <div style={{ fontWeight:700, fontSize:15 }}>{crop.cropName}</div>
                                <div style={{ fontSize:12, color:'var(--gray-400)', textTransform:'capitalize' }}>
                                  {crop.category} · {crop.subCategory}
                                </div>
                              </div>
                              <span style={{ background:'#D1FAE5', color:'#065F46', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:600, flexShrink:0 }}>
                                🟢 Ready
                              </span>
                            </div>
                            {crop.price && (
                              <div style={{ fontSize:13, color:'var(--green)', fontWeight:700, marginTop:4 }}>
                                ₹{crop.price} / {crop.priceUnit?.replace('per_','')}
                              </div>
                            )}
                            <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:4, display:'flex', gap:12 }}>
                              <span>👤 {crop.userId?.name || 'Farmer'}</span>
                              <span> {(crop.userId?.locationAddress || crop.locationAddress || '—').split(',')[0]}</span>
                            </div>
                            <div style={{ display:'flex', gap:8, marginTop:10 }}>
                              <button className="btn btn-primary btn-sm" style={{ flex:1 }}
                                onClick={e => { e.stopPropagation(); navigate(`/crop/${crop._id}`); }}>
                                👁 View Crop
                              </button>
                              <a href={`tel:+91${crop.userId?.mobile}`}
                                className="btn btn-outline btn-sm"
                                style={{ flex:1, justifyContent:'center', display:'flex' }}
                                onClick={e => e.stopPropagation()}>
                                📞 Call Farmer
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    })
                }
                {!readyLoading && readyCrops.length === 0 && (
                  <div style={{ textAlign:'center', padding:30, color:'var(--gray-400)' }}>
                    <div style={{ fontSize:32, marginBottom:8 }}></div>
                    <div>No crops ready to sell</div>
                    {(readyFilter.category || readyFilter.location) &&
                      <div style={{ fontSize:12, marginTop:4 }}>Try clearing filters</div>}
                  </div>
                )}
              </>
            )}

            {/* ── MY REQUESTS TAB ── */}
            {tab === 'myrequests' && (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                  <span style={{ fontSize:13, color:'var(--gray-400)' }}>{myRequests.length} open requests</span>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate('/requests/new')}>+ New</button>
                </div>
                {myRequests
                  .filter(r => !search ||
                    r.cropName?.toLowerCase().includes(search.toLowerCase()) ||
                    r.preferredLocation?.toLowerCase().includes(search.toLowerCase()))
                  .map(req => (
                    <div key={req._id} className="card"
                      style={{ padding:'12px 14px', marginBottom:8, borderLeft:'4px solid #3B82F6' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:15 }}>{req.cropName}</div>
                          <div style={{ fontSize:13, color:'var(--gray-500)' }}>
                            ⚖️ {req.quantity} {req.quantityUnit}
                            {req.offeredPrice && <span style={{ color:'var(--green)', fontWeight:600, marginLeft:6 }}>₹{req.offeredPrice}/{req.offeredPriceUnit?.replace('per_','')}</span>}
                          </div>
                        </div>
                        <span style={{ background:'#DBEAFE', color:'#1D4ED8', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:600 }}>
                          Open
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:'var(--gray-500)' }}> {req.preferredLocation || '—'}</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:2 }}>
                        🕐 {new Date(req.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                      </div>
                      {req.notes && <div style={{ fontSize:12, color:'var(--gray-500)', fontStyle:'italic', marginTop:4 }}>"{req.notes}"</div>}
                    </div>
                  ))
                }
                {myRequests.length === 0 && (
                  <div style={{ textAlign:'center', padding:30, color:'var(--gray-400)' }}>
                    <div style={{ fontSize:32, marginBottom:8 }}>📋</div>
                    <div>No open requests</div>
                    <button className="btn btn-primary" style={{ marginTop:12 }}
                      onClick={() => navigate('/requests/new')}>+ Create Request</button>
                  </div>
                )}
              </>
            )}

            {/* ── FARMERS TAB ── */}
            {tab === 'farmers' && (
              <>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <span style={{ fontSize:13, color:'var(--gray-400)' }}>{filterList(farmers).length} farmers</span>
                  <button className="btn btn-outline btn-sm" onClick={() => navigate('/marketplace')}>🛒 Marketplace</button>
                </div>
                {filterList(farmers).map(f => (
                  <div key={f._id} className="card"
                    style={{ padding:'12px 14px', marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--green)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:18, flexShrink:0 }}>
                      {f.name?.[0] || 'F'}
                    </div>
                    <div style={{ flex:1, cursor:'pointer', minWidth:0 }} onClick={() => navigate(`/farmer/${f._id}`)}>
                      <div style={{ fontWeight:600, fontSize:14 }}>{f.name}</div>
                      <div style={{ fontSize:12, color:'var(--gray-400)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                         {f.locationAddress || 'No location'}
                      </div>
                      <div style={{ fontSize:12, marginTop:2 }}>
                         {f.cropCount||0} crops
                        {f.readyCount > 0 && <span style={{ color:'var(--green)', marginLeft:8, fontWeight:600 }}>🟢 {f.readyCount} ready</span>}
                      </div>
                    </div>
                    <a href={`tel:+91${f.mobile}`} className="btn btn-outline btn-sm" style={{ flexShrink:0 }}>📞</a>
                  </div>
                ))}
                {filterList(farmers).length === 0 && <div style={{ textAlign:'center', padding:30, color:'var(--gray-400)' }}>No farmers found</div>}
              </>
            )}

            {/* ── BUYERS TAB ── */}
            {tab === 'buyers' && (
              <>
                <div style={{ marginBottom:12 }}>
                  <span style={{ fontSize:13, color:'var(--gray-400)' }}>{filterList(buyers).length} buyers</span>
                </div>
                {filterList(buyers).map(b => (
                  <div key={b._id} className="card"
                    style={{ padding:'12px 14px', marginBottom:8, display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--blue)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:18, flexShrink:0 }}>
                      {b.name?.[0] || 'B'}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:14 }}>{b.name}</div>
                      <div style={{ fontSize:12, color:'var(--gray-400)' }}> {b.locationAddress || 'No location'}</div>
                      <div style={{ fontSize:12, color:'var(--gray-400)' }}>📱 +91 {b.mobile}</div>
                    </div>
                    <a href={`tel:+91${b.mobile}`} className="btn btn-outline btn-sm"
                      style={{ flexShrink:0, borderColor:'var(--blue)', color:'var(--blue)' }}>📞</a>
                  </div>
                ))}
                {filterList(buyers).length === 0 && <div style={{ textAlign:'center', padding:30, color:'var(--gray-400)' }}>No buyers found</div>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}